import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  MobileHeaderCard,
  type MobileHeaderCardProps,
} from '~/components/MobileHeaderCard';
import { ModalType } from '~/components/Modal/interface';
import { useModal } from '~/hooks/useModal';
import { userStore } from '~/stores';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

import SeeAllButton from '../HeaderOrders/SeeAllButton';
import { getLabelDeclension } from '../HeaderOrders/utils';

import FavProductCard from './FavProductCard';
import styles from './HeaderFavs.module.scss';

const HeaderFavs = ({
  onTransitionEnd,
  className,
  overlayClassName,
  dropdownClassName,
  enableLabel = true,
  enableCaption = true,
  icon = 'heart',
}: Pick<
  MobileHeaderCardProps,
  | 'onTransitionEnd'
  | 'className'
  | 'overlayClassName'
  | 'dropdownClassName'
  | 'icon'
> & { enableLabel?: boolean; enableCaption?: boolean }) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const navigate = useNavigate();

  const count = favoritesStore.favorites.length;

  useEffect(() => {
    if (!userStore.isAuthorized) {
      return;
    }

    favoritesStore.fetchFavorites(true);
  }, [userStore.isAuthorized]);

  const handleClick = () => {
    if (!userStore.isAuthorized) {
      openModal(ModalType.Auth);
      return;
    }

    navigate('/favs');
  };

  const showFavoriteLabel = count > 0;

  return (
    <MobileHeaderCard
      observerKey={OBSERVER_KEY}
      icon={icon}
      caption={
        showFavoriteLabel && enableLabel && enableCaption
          ? t('favorites')
          : undefined
      }
      title={
        showFavoriteLabel && enableLabel
          ? `${count} ${getLabelDeclension(count)}`
          : undefined
      }
      isLoading={favoritesStore.favoritesStoreCRUDMeta.isLoading}
      onClick={handleClick}
      onTransitionEnd={onTransitionEnd}
      className={className}
      overlayClassName={overlayClassName}
      dropdownClassName={dropdownClassName}
      showChevron={enableLabel}
    >
      {showFavoriteLabel && (
        <>
          <div className={styles.root}>
            {favoritesStore.favorites.slice(0, 5).map((product) => (
              <FavProductCard
                key={product.id}
                product={product}
                observerKey={OBSERVER_KEY}
              />
            ))}
          </div>
          {favoritesStore.favorites.length > 5 && (
            <SeeAllButton
              observerKey={OBSERVER_KEY}
              link="/favs"
              label="seeAllFavs"
              className={styles.seeAllBtn}
            />
          )}
        </>
      )}
    </MobileHeaderCard>
  );
};

const OBSERVER_KEY = 'header.favs';

export default observer(HeaderFavs);
